import { useEffect, useState } from "react";
import { GlassCard, PageHeader, EmptyState, Skeleton } from "../components/shared";
import { 
    CheckSquare, 
    Circle, 
    CheckCircle2, 
    Plus, 
    Users, 
    Calendar, 
    Link as LinkIcon, 
    FileText, 
    ExternalLink,
    AlertCircle,
    Pencil,
    Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Tasks() {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Assignment Dialog States
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignForm, setAssignForm] = useState({
        title: "",
        description: "",
        assigned_to: "",
        due_date: "",
        category: "Engineering"
    });

    // Submission Dialog States
    const [submitOpen, setSubmitOpen] = useState(false);
    const [activeTask, setActiveTask] = useState(null);
    const [submitForm, setSubmitForm] = useState({
        submission_link: "",
        submission_notes: "",
        submission_file: "",
        submission_filename: ""
    });

    const isAssigner = ["super_admin", "hr", "manager", "lead"].includes(user?.role);
    const canEditDelete = ["super_admin", "hr", "lead"].includes(user?.role);

    // Edit Dialog States
    const [editingTask, setEditingTask] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        title: "",
        description: "",
        assigned_to: "",
        due_date: "",
        category: "Engineering"
    });

    const loadTasks = async () => {
        try {
            const { data } = await api.get("/tasks");
            setTasks(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const { data } = await api.get("/employees?limit=500&status=active");
            setEmployees(data.items || []);
        } catch (e) {
            console.error("Failed to load employees for task assignment", e);
        }
    };

    const handleEditClick = (t) => {
        setEditingTask(t);
        setEditForm({
            title: t.title,
            description: t.description,
            assigned_to: t.assigned_to,
            due_date: t.due_date ? t.due_date.split("T")[0] : "",
            category: t.category || "Engineering"
        });
        setEditOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        toast.loading("Updating task...", { id: "edit-task" });
        try {
            await api.patch(`/tasks/${editingTask.id}`, editForm);
            toast.success("Task updated successfully!", { id: "edit-task" });
            setEditOpen(false);
            setEditingTask(null);
            loadTasks();
        } catch (err) {
            toast.error(formatApiError(err), { id: "edit-task" });
        }
    };

    const handleDeleteTask = async (id) => {
        if (!window.confirm("Are you sure you want to delete this task?")) return;
        toast.loading("Deleting task...", { id: "delete-task" });
        try {
            await api.delete(`/tasks/${id}`);
            toast.success("Task deleted successfully!", { id: "delete-task" });
            loadTasks();
        } catch (err) {
            toast.error(formatApiError(err), { id: "delete-task" });
        }
    };

    useEffect(() => {
        if (!user) return;
        loadTasks();
        if (isAssigner) {
            loadEmployees();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleAssignSubmit = async (e) => {
        e.preventDefault();
        if (!assignForm.assigned_to) {
            toast.error("Please select an employee/intern to assign this task to");
            return;
        }

        toast.loading("Assigning task...", { id: "assign-task" });
        try {
            await api.post("/tasks", assignForm);
            toast.success("Task assigned successfully!", { id: "assign-task" });
            setAssignOpen(false);
            setAssignForm({
                title: "",
                description: "",
                assigned_to: "",
                due_date: "",
                category: "Engineering"
            });
            loadTasks();
        } catch (e) {
            toast.error(formatApiError(e), { id: "assign-task" });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Limit size to 4MB for base64 uploading
        if (file.size > 4 * 1024 * 1024) {
            toast.error("File is too large. Max size is 4MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSubmitForm(prev => ({
                ...prev,
                submission_file: reader.result,
                submission_filename: file.name
            }));
            toast.success(`Attached: ${file.name}`);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmitOpen = (task) => {
        setActiveTask(task);
        setSubmitForm({
            submission_link: "",
            submission_notes: "",
            submission_file: "",
            submission_filename: ""
        });
        setSubmitOpen(true);
    };

    const handleTaskSubmission = async (e) => {
        e.preventDefault();
        if (!submitForm.submission_link && !submitForm.submission_file) {
            toast.error("Please provide either a submission link or upload a file (zip/image)");
            return;
        }

        toast.loading("Submitting task...", { id: "submit-task" });
        try {
            await api.post(`/tasks/${activeTask.id}/submit`, {
                submission_link: submitForm.submission_link,
                submission_notes: submitForm.submission_notes,
                submission_file: submitForm.submission_file || null
            });
            toast.success("Task submitted successfully!", { id: "submit-task" });
            setSubmitOpen(false);
            loadTasks();
        } catch (e) {
            toast.error(formatApiError(e), { id: "submit-task" });
        }
    };

    if (loading) {
        return (
            <div className="space-y-8">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-44 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader 
                title="Tasks" 
                subtitle={isAssigner ? "Assign operations and track completion workflows." : "Submit deliverables and check your daily action checklist."}
                actions={
                    isAssigner && (
                        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full bg-teal-600 hover:bg-teal-700 text-white">
                                    <Plus className="w-4 h-4 mr-1" /> Assign task
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Assign Task Deliverable</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleAssignSubmit} className="space-y-4 pt-2">
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700">Task Title</Label>
                                        <Input required value={assignForm.title} onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })} className="mt-1" placeholder="e.g. Build Login UI" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700">Task Description / Instructions</Label>
                                        <textarea 
                                            required 
                                            value={assignForm.description} 
                                            onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })}
                                            className="mt-1 w-full min-h-[80px] rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-sm"
                                            placeholder="Provide detail on what needs to be submitted (zip, link, screenshot)"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700">Assign To</Label>
                                        <select 
                                            value={assignForm.assigned_to} 
                                            onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })} 
                                            className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm"
                                        >
                                            <option value="">Select Employee / Intern</option>
                                            {employees.map(emp => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.name} ({emp.designation || emp.role})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Category</Label>
                                            <select value={assignForm.category} onChange={(e) => setAssignForm({ ...assignForm, category: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                                                <option value="Engineering">Engineering</option>
                                                <option value="Documents">Documents</option>
                                                <option value="HR Operations">HR Operations</option>
                                                <option value="Design">Design</option>
                                                <option value="Finance">Finance</option>
                                                <option value="General">General</option>
                                            </select>
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Due Date</Label>
                                            <Input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} className="mt-1" />
                                        </div>
                                    </div>
                                    <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold mt-4">Assign Task</Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )
                }
            />

            <div className="max-w-4xl space-y-6">
                <GlassCard className="p-6 border border-teal-100/50 dark:border-teal-900/50">
                    <h3 className="font-heading font-bold text-lg mb-6 text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <CheckSquare className="w-5 h-5 text-teal-600" />
                        {isAssigner ? "All Managed Tasks" : "My Assigned Tasks"}
                    </h3>

                    <div className="space-y-4">
                        {tasks.map((t) => (
                            <div 
                                key={t.id} 
                                className={`p-5 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4
                                ${t.status === "completed" 
                                    ? "bg-slate-50/40 dark:bg-slate-900/10 border-slate-100 dark:border-slate-800/80" 
                                    : "bg-white dark:bg-slate-900/40 border-black/5 dark:border-white/5 hover:shadow-sm"
                                }`}
                            >
                                <div className="space-y-2 flex-1">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h4 className={`text-base font-semibold ${t.status === "completed" ? "text-slate-500 line-through" : "text-slate-800 dark:text-slate-100"}`}>
                                            {t.title}
                                        </h4>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase
                                            ${t.status === "completed" 
                                                ? "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500" 
                                                : "bg-teal-50 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400 border border-teal-200/50"
                                            }`}>
                                            {t.category}
                                        </span>
                                        {t.status === "completed" ? (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200/30">Completed</span>
                                        ) : (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200/30">Pending</span>
                                        )}
                                    </div>
                                    
                                    <p className={`text-sm ${t.status === "completed" ? "text-slate-400" : "text-slate-600 dark:text-slate-300"}`}>
                                        {t.description}
                                    </p>

                                    <div className="flex items-center gap-4 text-xs text-slate-400 font-medium pt-1 flex-wrap">
                                        {isAssigner && (
                                            <span className="flex items-center gap-1">
                                                <Users className="w-3.5 h-3.5 text-teal-600" /> Assigned to: <strong className="text-slate-500 dark:text-slate-300">{t.assigned_to_name}</strong>
                                            </span>
                                        )}
                                        {!isAssigner && (
                                            <span className="flex items-center gap-1">
                                                <Users className="w-3.5 h-3.5 text-teal-600" /> Assigned by: <strong className="text-slate-500 dark:text-slate-300">{t.assigned_by_name}</strong>
                                            </span>
                                        )}
                                        {t.due_date && (
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3.5 h-3.5" /> Due date: {new Date(t.due_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </span>
                                        )}
                                    </div>

                                    {/* Submission Deliverables Details */}
                                    {t.status === "completed" && (
                                        <div className="mt-3 p-3 bg-slate-100/50 dark:bg-slate-900/40 rounded-xl space-y-1.5 border border-black/5 dark:border-white/5">
                                            <span className="text-[11px] font-bold text-slate-400 uppercase">Submission Deliverables</span>
                                            {t.submission_link && (
                                                <div className="flex items-center gap-1.5 text-xs text-primary font-semibold">
                                                    <LinkIcon className="w-3.5 h-3.5 text-teal-600" />
                                                    <a href={t.submission_link} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-0.5">
                                                        Submission Link <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                </div>
                                            )}
                                            {t.submission_file && (
                                                <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 font-semibold">
                                                    <FileText className="w-3.5 h-3.5 text-teal-600" />
                                                    {t.submission_file.startsWith("data:") ? (
                                                        <a href={t.submission_file} download={t.submission_filename || "submitted_file"} className="hover:underline text-teal-600 dark:text-teal-400">
                                                            Download Attachment ({t.submission_filename || "deliverable"})
                                                        </a>
                                                    ) : (
                                                        <span>Submitted Attachment: {t.submission_file}</span>
                                                    )}
                                                </div>
                                            )}
                                            {t.submission_notes && (
                                                <p className="text-xs text-slate-500 italic">
                                                    Notes: "{t.submission_notes}"
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 shrink-0">
                                    {t.status === "completed" ? (
                                        <CheckCircle2 className="w-8 h-8 text-emerald-500" strokeWidth={1.5} />
                                    ) : (
                                        !isAssigner ? (
                                            <Button 
                                                onClick={() => handleSubmitOpen(t)}
                                                className="rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-xs px-4"
                                            >
                                                Submit Deliverables
                                            </Button>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-xs text-slate-400 font-semibold bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full">
                                                <Circle className="w-3.5 h-3.5 text-amber-500 animate-pulse" /> Pending
                                            </div>
                                        )
                                    )}

                                    {canEditDelete && (
                                        <div className="flex items-center gap-1 ml-2 border-l border-slate-100 dark:border-slate-800 pl-2">
                                            <button 
                                                type="button"
                                                onClick={() => handleEditClick(t)} 
                                                className="p-1 text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded transition"
                                                title="Edit Task"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => handleDeleteTask(t.id)} 
                                                className="p-1 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded transition"
                                                title="Delete Task"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {tasks.length === 0 && (
                            <EmptyState
                                title="No tasks found"
                                description={isAssigner ? "You haven't assigned any operation workflows yet." : "You have no active checklists or assigned deliverables."}
                                icon={CheckSquare}
                            />
                        )}
                    </div>
                </GlassCard>
            </div>

            {/* Submission Deliverables Dialog */}
            <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle>Submit Deliverables</DialogTitle>
                    </DialogHeader>
                    {activeTask && (
                        <div className="space-y-4 pt-2">
                            <div className="p-3 bg-teal-50/50 dark:bg-teal-950/10 border border-teal-100 dark:border-teal-900/30 rounded-xl space-y-1">
                                <span className="text-xs font-bold text-teal-600 uppercase">Task Title</span>
                                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{activeTask.title}</h4>
                                <p className="text-xs text-slate-500 mt-1">{activeTask.description}</p>
                            </div>
                            
                            <form onSubmit={handleTaskSubmission} className="space-y-4">
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Submission Link (e.g. GitHub, Google Drive)</Label>
                                    <Input 
                                        type="url"
                                        value={submitForm.submission_link} 
                                        onChange={(e) => setSubmitForm({ ...submitForm, submission_link: e.target.value })} 
                                        className="mt-1" 
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Upload Zip / Deliverable Image (Max 4MB)</Label>
                                    <div className="mt-1 flex items-center justify-between border border-input rounded-xl px-3 py-2 bg-background">
                                        <input 
                                            type="file" 
                                            accept=".zip,.png,.jpg,.jpeg,.pdf"
                                            onChange={handleFileChange}
                                            className="text-xs w-full cursor-pointer file:mr-4 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                                        />
                                    </div>
                                    {submitForm.submission_filename && (
                                        <span className="text-[11px] text-teal-600 font-semibold block mt-1">
                                            ✓ Ready: {submitForm.submission_filename}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Submission Notes / Message</Label>
                                    <textarea 
                                        value={submitForm.submission_notes} 
                                        onChange={(e) => setSubmitForm({ ...submitForm, submission_notes: e.target.value })}
                                        className="mt-1 w-full min-h-[80px] rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-sm"
                                        placeholder="Add notes for your Team Lead / Admin..."
                                    />
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                                    <span>Provide either a submission URL OR upload a zip/image.</span>
                                </div>
                                <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold mt-4">Submit Deliverables</Button>
                            </form>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Task Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle>Edit Task Details</DialogTitle>
                    </DialogHeader>
                    {editingTask && (
                        <form onSubmit={handleEditSubmit} className="space-y-4 pt-2">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Task Title</Label>
                                <Input required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Task Description / Instructions</Label>
                                <textarea 
                                    required 
                                    value={editForm.description} 
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    className="mt-1 w-full min-h-[80px] rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-sm"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Assign To</Label>
                                <select 
                                    value={editForm.assigned_to} 
                                    onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })} 
                                    className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm"
                                >
                                    <option value="">Select Employee / Intern</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.name} ({emp.designation || emp.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Category</Label>
                                    <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                                        <option value="Engineering">Engineering</option>
                                        <option value="Documents">Documents</option>
                                        <option value="HR Operations">HR Operations</option>
                                        <option value="Design">Design</option>
                                        <option value="Finance">Finance</option>
                                        <option value="General">General</option>
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Due Date</Label>
                                    <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} className="mt-1" />
                                </div>
                            </div>
                            <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold mt-4">Save Changes</Button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
